// JQ4-14 — US-ORG-006: Daily Summary Dashboard
//
// Replaces the placeholder KPI strip with real-time metrics polled from the
// backend every 30 seconds.  The hero section and quick-actions are preserved
// verbatim from the original Sprint-1/IAM placeholder.
//
// Design decisions implemented here:
//   D5  — per-query refetchInterval (30s), no global QueryClient mutation
//   D5  — placeholderData: keepPreviousData → no skeleton flash on bg refetch
//   D5  — isLoading drives skeleton (first load only); isFetching drives the
//         "Updating…" indicator during background polls
//   D6  — zeros-by-construction: all four fields are non-nullable integers
//         from the backend; render "0" directly, never "—" or blank

import { getDailySummary, listLocations } from '@jq4race/api-client';
import { useTranslation } from '@jq4race/i18n';
import {
  type DailySummaryResponse,
  DEFAULT_CURRENCY,
  type LocationResponse,
} from '@jq4race/shared';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  formatCents,
  Skeleton,
  StatCard,
} from '@jq4race/ui';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import {
  ArrowRight,
  Car,
  KeyRound,
  type LucideIcon,
  RefreshCw,
  ShieldCheck,
  TriangleAlert,
  UserCheck,
  Users as UsersIcon,
} from 'lucide-react';
import { type ReactNode, useState } from 'react';
import { translateApiError } from '../../../../shared/errors/translateApiError';
import { enumLabel } from '../../../../shared/i18n/enum-label';
import { useAuthState } from '../../../auth/ui/auth-state-context';
import { EntityCountStrip } from '../components/EntityCountStrip';
import { OrgAnalyticsOverviewSection } from '../components/OrgAnalyticsOverviewSection';
import { RecentAuditFeed } from '../components/RecentAuditFeed';
import { RecentTicketsFeed } from '../components/RecentTicketsFeed';
import { SubscriptionUsageCard } from '../components/SubscriptionUsageCard';

// ── Main page ────────────────────────────────────────────────────────────────

export function DashboardPage() {
  const { t } = useTranslation('dashboard');
  const { session } = useAuthState();
  const navigate = useNavigate();

  const firstName = session?.user.email?.split('@')[0]?.split(/[._-]/)[0] ?? '';
  const greeting = firstName
    ? t('hero.greetingNamed', { name: capitalize(firstName) })
    : t('hero.greeting');

  function onUsers() {
    navigate({ to: '/dashboard/users' });
  }

  // ── Location list query ──────────────────────────────────────────────────
  const {
    data: locationsData,
    isLoading: locationsLoading,
    isError: locationsError,
  } = useQuery({
    queryKey: ['locations'],
    queryFn: () => listLocations(),
  });

  const locations: LocationResponse[] = locationsData?.locations ?? [];

  // Sort alphabetically; initialise with first location once loaded.
  const sortedLocations = [...locations].sort((a, b) => a.name.localeCompare(b.name));

  const [selectedLocationId, setSelectedLocationId] = useState<string | undefined>(undefined);

  // Derive the effective selected id: use state if valid, else fall back to
  // the first sorted location so we have something to fetch immediately once
  // the list arrives.
  const firstLocationId = sortedLocations[0]?.id;
  const effectiveLocationId = selectedLocationId ?? firstLocationId;

  // ── Daily summary query ──────────────────────────────────────────────────
  const {
    data: summary,
    isLoading: summaryLoading,
    isFetching: summaryFetching,
    isError: summaryError,
    error: summaryErrorObj,
  } = useQuery({
    queryKey: ['daily-summary', effectiveLocationId],
    queryFn: () => getDailySummary(effectiveLocationId as string),
    enabled: !!effectiveLocationId,
    refetchInterval: 30_000,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
    placeholderData: keepPreviousData,
  });

  const selectedLocation = sortedLocations.find((l) => l.id === effectiveLocationId);
  const currency = selectedLocation?.currency ?? DEFAULT_CURRENCY;

  // ── Render helpers ───────────────────────────────────────────────────────
  // Show the skeleton whenever we have no summary yet — this covers first load
  // AND the window where the query is still disabled (no location resolved yet),
  // during which React Query v5 reports isLoading === false (fetchStatus 'idle')
  // even though `summary` is undefined.  Reading `.checkInsToday` off undefined
  // here was the crash for users landing before locations finished loading.
  const showKpiSkeleton = !summary;
  const showUpdating = summaryFetching && !summaryLoading;

  return (
    <div className="space-y-12">
      {/* ----- Hero ----- */}
      <section className="grid items-start gap-10 lg:grid-cols-[minmax(0,1.6fr)_minmax(0,1fr)]">
        <div>
          <h2 className="font-editorial text-[40px] font-medium leading-[1.05] tracking-tight text-jq-navy sm:text-[52px]">
            {greeting}
          </h2>
          <p className="mt-4 max-w-xl text-[15px] leading-relaxed text-jq-graphite">
            {t('hero.description')}
          </p>

          <div className="mt-7 flex flex-wrap items-center gap-3">
            <Button onClick={onUsers}>
              {t('hero.manageUsers')}
              <ArrowRight className="h-4 w-4" strokeWidth={1.5} />
            </Button>
            <Button variant="outline" onClick={() => navigate({ to: '/dashboard/analytics' })}>
              {t('hero.openReports')}
            </Button>
          </div>
        </div>

        <Card className="border-jq-sand/70 bg-jq-surface-0 shadow-sm">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="font-editorial text-[18px] font-medium text-jq-navy">
                {t('session.title')}
              </CardTitle>
              <Badge variant="info">{enumLabel(t, 'userRole', session?.user.role)}</Badge>
            </div>
          </CardHeader>
          <CardContent>
            <dl className="grid gap-3 text-[13px]">
              <SessionRow label={t('session.userId')} value={truncate(session?.user.id)} mono />
              <SessionRow
                label={t('session.organization')}
                value={truncate(session?.user.organizationId)}
                mono
              />
              <SessionRow label={t('session.email')} value={session?.user.email ?? '—'} />
            </dl>
          </CardContent>
        </Card>
      </section>

      {/* ----- Today · At a glance ----- */}
      <section>
        {/* Section header: eyebrow + location switcher + updating indicator */}
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <p className="jq-eyebrow">{t('today.eyebrow')}</p>

          <div className="flex items-center gap-3">
            {showUpdating && (
              <span className="flex items-center gap-1.5 text-[12px] text-jq-graphite">
                <RefreshCw className="h-3 w-3 animate-spin" strokeWidth={1.75} />
                {t('today.updating')}
              </span>
            )}

            {/* Location switcher */}
            {locationsLoading ? (
              <Skeleton className="h-8 w-48" />
            ) : locationsError ? (
              <span className="text-[12px] text-jq-danger">{t('today.locationsError')}</span>
            ) : sortedLocations.length === 0 ? null : (
              <div className="flex items-center gap-2">
                <label
                  htmlFor="location-switcher"
                  className="text-[12px] font-medium text-jq-graphite"
                >
                  {t('today.locationLabel')}
                </label>
                <select
                  id="location-switcher"
                  value={effectiveLocationId ?? ''}
                  onChange={(e) => setSelectedLocationId(e.target.value)}
                  className="h-8 rounded-md border border-input bg-transparent px-2 py-0 text-[13px] shadow-sm focus:outline-none focus:ring-1 focus:ring-ring"
                >
                  {sortedLocations.map((loc) => (
                    <option key={loc.id} value={loc.id}>
                      {loc.name}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>
        </div>

        {/* Empty locations state */}
        {!locationsLoading && !locationsError && sortedLocations.length === 0 ? (
          <NoLocationsState />
        ) : /* KPI error state */
        summaryError ? (
          <KpiErrorState error={summaryErrorObj} />
        ) : (
          /* KPI grid */
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <StatCard
              icon={UsersIcon}
              label={t('kpi.checkInsToday')}
              value={
                showKpiSkeleton ? null : (summary as DailySummaryResponse).checkInsToday.toString()
              }
            />
            <StatCard
              icon={Car}
              label={t('kpi.activeNow')}
              value={
                showKpiSkeleton
                  ? null
                  : (summary as DailySummaryResponse).activeTicketsNow.toString()
              }
            />
            <StatCard
              icon={KeyRound}
              label={t('kpi.revenueToday')}
              value={
                showKpiSkeleton
                  ? null
                  : formatCents((summary as DailySummaryResponse).revenueToday, currency)
              }
            />
            <StatCard
              icon={UserCheck}
              label={t('kpi.pendingRetrieval')}
              value={
                showKpiSkeleton
                  ? null
                  : (summary as DailySummaryResponse).pendingRetrievalCount.toString()
              }
            />
          </div>
        )}
      </section>

      {/* ----- Last 30 days · Organization (role-based-dashboards Phase 6) ----- */}
      <OrgAnalyticsOverviewSection locations={sortedLocations} />

      {/* ----- Entity counts ----- */}
      <section>
        <p className="jq-eyebrow mb-4">{t('atAGlance')}</p>
        <EntityCountStrip />
      </section>

      {/* ----- Subscription + recent activity ----- */}
      <section className="grid gap-4 lg:grid-cols-3">
        <SubscriptionUsageCard />
        <RecentAuditFeed />
        <RecentTicketsFeed />
      </section>

      {/* ----- Quick actions ----- */}
      <section>
        <p className="jq-eyebrow mb-4">{t('quickActions.eyebrow')}</p>
        <div className="grid gap-4 sm:grid-cols-2">
          <ActionCard
            icon={UsersIcon}
            title={t('quickActions.inviteTeammateTitle')}
            body={t('quickActions.inviteTeammateBody')}
            cta={t('quickActions.openUsers')}
            onClick={onUsers}
          />
          <ActionCard
            icon={ShieldCheck}
            title={t('quickActions.auditSessionsTitle')}
            body={t('quickActions.auditSessionsBody')}
            cta={t('quickActions.openUsers')}
            onClick={onUsers}
          />
        </div>
      </section>
    </div>
  );
}

// ── Sub-components ────────────────────────────────────────────────────────────

function NoLocationsState() {
  const { t } = useTranslation('dashboard');
  return (
    <div className="rounded-sm border border-jq-sand/60 bg-jq-surface-0 px-6 py-10 text-center">
      <p className="text-[14px] text-jq-graphite">{t('today.noLocationsTitle')}</p>
      <p className="mt-1 text-[13px] text-jq-stone">{t('today.noLocationsBody')}</p>
    </div>
  );
}

function KpiErrorState({ error }: { error: Error | null }) {
  const { t } = useTranslation('dashboard');
  const message = translateApiError(error, t, { fallback: t('today.kpiError') });
  return (
    <div
      role="alert"
      className="flex items-start gap-3 rounded-sm border border-jq-danger/40 bg-jq-danger/10 px-4 py-4 text-[13px] text-jq-danger"
    >
      <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0" strokeWidth={1.75} />
      <p className="leading-snug">{message}</p>
    </div>
  );
}

function SessionRow({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <dt className="text-jq-graphite">{label}</dt>
      <dd className={mono ? 'font-mono text-[12px] text-jq-charcoal' : 'text-jq-charcoal'}>
        {value}
      </dd>
    </div>
  );
}

function ActionCard({
  icon: Icon,
  title,
  body,
  cta,
  onClick,
}: {
  icon: LucideIcon;
  title: string;
  body: string;
  cta?: ReactNode;
  onClick?: () => void;
}) {
  const isInteractive = !!onClick;
  return (
    <Card
      className={`relative overflow-hidden border-jq-sand/70 bg-jq-surface-0 shadow-sm transition ${
        isInteractive ? 'cursor-pointer hover:-translate-y-[1px] hover:shadow-md' : 'opacity-75'
      }`}
      onClick={isInteractive ? onClick : undefined}
      role={isInteractive ? 'button' : undefined}
      tabIndex={isInteractive ? 0 : undefined}
      onKeyDown={(e) => {
        if (!isInteractive) return;
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick?.();
        }
      }}
    >
      <span aria-hidden className="absolute inset-x-0 top-0 h-px bg-jq-gold/30" />
      <CardContent className="space-y-2 p-5">
        <span className="inline-flex h-9 w-9 items-center justify-center rounded-md bg-jq-surface-3 text-jq-navy">
          <Icon className="h-4 w-4" strokeWidth={1.5} />
        </span>
        <h3 className="font-editorial text-[18px] font-medium leading-snug text-jq-navy">
          {title}
        </h3>
        <p className="text-[13px] leading-relaxed text-jq-graphite">{body}</p>
        {cta ? (
          <p className="pt-1 text-[12px] font-medium uppercase tracking-widest text-jq-gold-deep">
            {cta}
          </p>
        ) : null}
      </CardContent>
    </Card>
  );
}

// ── Utilities ─────────────────────────────────────────────────────────────────

function truncate(value: string | null | undefined): string {
  if (!value) return '—';
  return `${value.slice(0, 8)}…`;
}

function capitalize(value: string): string {
  if (!value) return value;
  return value.charAt(0).toUpperCase() + value.slice(1);
}
